import {
  IDestinationEntity,
  IDestinationEntityJobRunOptions,
  IGetRecordResponse,
  IPracticeMigration,
  ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  BASIC_MEDICAL_HISTORY_FORM_SCHEMA,
  Brand,
  CustomFormConfiguration,
  CustomFormContent,
  toMedicalHistoryForm,
} from '@principle-theorem/principle-core';
import {
  snapshot,
  snapshotCombineLatest,
  toMomentTz,
  toTimestamp,
  WithRef,
} from '@principle-theorem/shared';
import { get, isEmpty } from 'lodash';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  BasePatientMedicalHistoryDestinationEntity,
  IPatientMedicalHistoryFormSubmission,
  IPatientMedicalHistoryJobData,
} from '../../../destination/entities/patient-medical-histories';
import {
  getValidMedicalHistoryFormOptions,
  setCustomFormValue,
} from '../../../mappings/allergy-to-medical-history';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import { D4WPatient, PatientSourceEntity } from '../../source/entities/patient';
import {
  D4WPatientAllergy,
  PatientAllergySourceEntity,
} from '../../source/entities/patient-allergy';
import {
  D4WPatientMedicalCondition,
  PatientMedicalConditionSourceEntity,
} from '../../source/entities/patient-medical-condition';
import { D4WAllergyMappingHandler } from '../mappings/allergy-to-medical-history';
import { D4WMedicalConditionToMedicalHistoryMappingHandler } from '../mappings/medical-condition-to-medical-history';
import { PatientDestinationEntity } from './patients';

export type JobData = IPatientMedicalHistoryJobData<D4WPatient>;

export class D4WPatientMedicalHistoryDestinationEntity extends BasePatientMedicalHistoryDestinationEntity<
  D4WPatient,
  JobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    medicalConditions: new PatientMedicalConditionSourceEntity(),
    allergies: new PatientAllergySourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    medicalConditions: new D4WMedicalConditionToMedicalHistoryMappingHandler(),
    allergies: new D4WAllergyMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<JobData[]> {
    const brand$ = PracticeMigration.brand$(migration);
    const medicalConditions$ =
      this.customMappings.medicalConditions.getRecords(translationMap);
    const allergies$ = this.customMappings.allergies.getRecords(translationMap);

    return snapshotCombineLatest([brand$, medicalConditions$, allergies$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.patientSourceEntity,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([sourcePatients, [brand, medicalConditions, allergies]]) =>
        sourcePatients.map((sourcePatient) => ({
          sourcePatient,
          brand,
          medicalConditions,
          allergies,
        }))
      )
    );
  }

  async buildFormSubmission(
    migration: WithRef<IPracticeMigration>,
    data: JobData
  ): Promise<IPatientMedicalHistoryFormSubmission | undefined> {
    const sourcePatientId = data.sourcePatient.data.patient_id.toString();
    const patientConditions =
      await this.sourceEntities.medicalConditions.filterRecords(
        migration,
        'patient_id',
        sourcePatientId
      );

    const patientAllergies = await this.sourceEntities.allergies.filterRecords(
      migration,
      'patient_id',
      sourcePatientId
    );

    if (!patientConditions.length && !patientAllergies.length) {
      return;
    }

    const config = await snapshot(Brand.medicalHistoryFormConfig$(data.brand));
    const content = config
      ? await CustomFormConfiguration.getContent(config)
      : undefined;
    const jsonSchemaForm = content
      ? CustomFormContent.getJsonSchemaForm(content)
      : {};
    const form = toMedicalHistoryForm(jsonSchemaForm);
    const schema = form?.schema ?? BASIC_MEDICAL_HISTORY_FORM_SCHEMA;
    let formData = {};

    getValidMedicalHistoryFormOptions(schema).map((formOption) => {
      let patientHasAllergy: IGetRecordResponse<D4WPatientAllergy> | undefined;
      let mappedAllergy: ITranslationMap<object, string> | undefined;

      for (const allergyMapping of data.allergies) {
        if (allergyMapping.destinationValue?.value !== formOption.path) {
          continue;
        }

        const foundAllergy = patientAllergies.find(
          (patientAllergy) =>
            patientAllergy.data.allergy_id.toString() ===
              allergyMapping.sourceIdentifier &&
            !isEmpty(allergyMapping.destinationValue?.value)
        );

        if (foundAllergy) {
          patientHasAllergy = foundAllergy;
          mappedAllergy = allergyMapping;
          break;
        }
      }

      if (patientHasAllergy && mappedAllergy?.destinationValue) {
        formData = setCustomFormValue(
          formOption,
          formData,
          true,
          mappedAllergy.associatedValue?.toString(),
          patientHasAllergy.data.description
        );
      }

      let patientHasCondition:
        | IGetRecordResponse<D4WPatientMedicalCondition>
        | undefined;
      let mappedCondition: ITranslationMap<object, string> | undefined;

      for (const conditionMapping of data.medicalConditions) {
        if (conditionMapping.destinationValue?.value !== formOption.path) {
          continue;
        }

        const foundCondition = patientConditions.find(
          (patientCondition) =>
            patientCondition.data.condition_id.toString() ===
              conditionMapping.sourceIdentifier &&
            !isEmpty(conditionMapping.destinationValue?.value)
        );
        if (foundCondition) {
          patientHasCondition = foundCondition;
          mappedCondition = conditionMapping;
          break;
        }
      }

      if (patientHasCondition && mappedCondition?.destinationValue) {
        formData = setCustomFormValue(
          formOption,
          formData,
          true,
          mappedCondition.associatedValue?.toString(),
          patientHasCondition.data.description
        );
      }

      if (get(formData, formOption.path, undefined)) {
        return;
      }

      formData = setCustomFormValue(formOption, formData);
    });

    const backupDate = toMomentTz(
      migration.configuration.backupDate,
      migration.configuration.timezone
    );

    return {
      ...form,
      data: formData,
      // TODO: set this to the migration date manually for Badra
      date: toTimestamp(backupDate),
      disableChanges: true,
    };
  }
}
